/**
 * Audio File Loader for Iron Meridian
 * 
 * Loads and manages audio files for music and ambient sounds.
 * All audio files should be CC0/Public Domain for commercial use.
 * 
 * Recommended CC0 Audio Sources (No Attribution Required):
 * - https://pixabay.com/music/ - Royalty-free music
 * - https://freesound.org/ - CC0 sound effects and ambience  
 * - https://www.soundjay.com/ - Free sound effects
 * - https://mixkit.co/free-stock-music/ - Free music
 */

// =====================================================================
// AUDIO FILE PATHS
// =====================================================================

// Base path for audio files
const AUDIO_BASE_PATH = '/audio';

// Music tracks - ambient/atmospheric for strategy games
export const MUSIC_TRACKS = {
  strategic: `${AUDIO_BASE_PATH}/music/strategic-ambient.mp3`,
  victory: `${AUDIO_BASE_PATH}/music/victory.mp3`,
};

// Ambient sounds - background atmosphere
export const AMBIENT_TRACKS = {
  warRoom: `${AUDIO_BASE_PATH}/ambient/war-room.mp3`,
  tension: `${AUDIO_BASE_PATH}/ambient/tension.mp3`,
  command: `${AUDIO_BASE_PATH}/ambient/command-center.mp3`,
};

// =====================================================================
// AUDIO LOADER CLASS
// =====================================================================

class AudioFileLoader {
  constructor() {
    this.bufferCache = new Map();
    this.loadingPromises = new Map();
  }

  /**
   * Load an audio file and decode it
   * @param {AudioContext} ctx - Web Audio context
   * @param {string} url - URL to the audio file
   * @returns {Promise<AudioBuffer>}
   */
  async loadAudio(ctx, url) {
    // Return cached buffer if available
    if (this.bufferCache.has(url)) {
      return this.bufferCache.get(url);
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url);
    }

    // Start loading
    const loadPromise = this._fetchAndDecode(ctx, url);
    this.loadingPromises.set(url, loadPromise);

    try {
      const buffer = await loadPromise;
      this.bufferCache.set(url, buffer);
      this.loadingPromises.delete(url);
      return buffer;
    } catch (error) {
      this.loadingPromises.delete(url);
      throw error;
    }
  }

  /**
   * Fetch and decode audio file
   */
  async _fetchAndDecode(ctx, url) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${url}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
      
      console.log(`[AudioLoader] Loaded: ${url}`);
      return audioBuffer;
    } catch (error) {
      console.warn(`[AudioLoader] Failed to load ${url}:`, error.message);
      return null;
    }
  }

  /**
   * Preload multiple audio files
   */
  async preloadAll(ctx, urls) {
    const promises = urls.map(url => this.loadAudio(ctx, url).catch(() => null));
    return Promise.all(promises);
  }

  /**
   * Check if an audio file exists
   */
  async checkExists(url) {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Clear the buffer cache
   */
  clearCache() {
    this.bufferCache.clear();
  }
}

// Singleton instance
export const audioLoader = new AudioFileLoader();

// =====================================================================
// PLAYBACK HELPERS
// =====================================================================

/**
 * Create a looping audio source from a buffer
 * @param {AudioContext} ctx
 * @param {AudioBuffer} buffer
 * @param {GainNode} destination
 * @param {Object} options
 * @returns {Object} Control object with stop() method
 */
export function createLoopingSource(ctx, buffer, destination, options = {}) {
  if (!ctx || !buffer || !destination) return null;

  const {
    fadeInDuration = 2,
    fadeOutDuration = 2,
    loop = true,
    volume = 1,
  } = options;

  const now = ctx.currentTime;

  // Create source
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = loop;

  // Create gain for fade control
  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(volume, now + fadeInDuration);

  // Connect
  source.connect(gainNode);
  gainNode.connect(destination);

  // Start playback
  source.start(now);

  // Return control object
  return {
    source,
    gainNode,
    stop: () => {
      const fadeStart = ctx.currentTime;
      gainNode.gain.setValueAtTime(gainNode.gain.value, fadeStart);
      gainNode.gain.linearRampToValueAtTime(0, fadeStart + fadeOutDuration);

      setTimeout(() => {
        try {
          source.stop();
          source.disconnect();
          gainNode.disconnect();
        } catch (e) {
          // Already stopped
        }
      }, fadeOutDuration * 1000 + 100);
    }
  };
}

export default audioLoader;
